import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SpaceProvider, useSpaces } from './context/SpaceContext';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SpacePage from './pages/SpacePage';
import InboxPage from './pages/InboxPage';
import CalendarPage from './pages/CalendarPage';
import FilesPage from './pages/FilesPage';
import MailboxesPage from './pages/MailboxesPage';
import ContactsPage from './pages/ContactsPage';
import MessagesPage from './pages/MessagesPage';
import AdminPage from './pages/AdminPage';
import AccountPage from './pages/AccountPage';
import ClientsPage from './pages/ClientsPage';
import BusinessesPage from './pages/BusinessesPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import TasksPage from './pages/TasksPage';
import MileagePage from './pages/MileagePage';
import TimePage from './pages/TimePage';
import WrikeSupplyRequestsPage from './pages/WrikeSupplyRequestsPage';
import './index.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading...</div>;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : children;
}

function SpaceSlugRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { spaces, loading } = useSpaces();

  useEffect(() => {
    const slug = searchParams.get('slug');

    if (!slug) {
      navigate('/', { replace: true });
      return;
    }

    if (loading) {
      return;
    }

    const matchedSpace = spaces.find((space) => space.slug === slug);
    if (matchedSpace?._id) {
      navigate(`/spaces/${matchedSpace._id}`, { replace: true });
      return;
    }

    navigate('/', { replace: true });
  }, [searchParams, navigate, spaces, loading]);

  return <div className="app-loading">Resolving space...</div>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <SpaceProvider>
              <MainLayout />
            </SpaceProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="spaces" element={<SpaceSlugRedirect />} />
        <Route path="spaces/:spaceId" element={<SpacePage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="files" element={<FilesPage />} />
        <Route path="mailboxes" element={<MailboxesPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:clientId" element={<ClientsPage />} />
        <Route path="businesses" element={<BusinessesPage />} />
        <Route path="businesses/:businessId" element={<BusinessesPage />} />
        <Route path="projects" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="mileage" element={<MileagePage />} />
        <Route path="time" element={<TimePage />} />
        <Route path="wrike/supply-requests" element={<WrikeSupplyRequestsPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
